import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/pro-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-light-svg-icons";
import { Image, Sponsor, SponsorService } from "../../../../client/api";
import { ClavaContext } from "../../../../config/contexts";
import AsideModal from "../../../components/AsideModal";
import { translate } from "../../../../config/translator";
import { errorLogging } from "../../../../config/utils";
import ClavaTextInput from "../../../components/ClavaTextInput";
import ClavaImage from "../../../components/ClavaImage";
import useBoolState from "../../../../hooks/useBoolState";
import FileUploader from "../../../components/FileUploader/FileUploader";
import { callClientFunc } from "../../../../client";
import { IDType } from "../../../../config/types";
import { trackAmplitude } from "../../../../config/amplitude";
import loadingGif from "../../../../assets/images/loading.gif";

const CreateEditSponsor: React.FC<{
  sponsor?: Sponsor;
  teamId: IDType;
  close: (sponsor?: Sponsor | IDType) => void;
}> = ({ sponsor, teamId, close }) => {
  const { l } = useContext(ClavaContext);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [imagePicker, toggleImagePicker] = useBoolState(false);
  const [name, setName] = useState(sponsor?.name ?? "");
  const [url, setUrl] = useState(sponsor?.url ?? "");
  const nameInvalid = useRef(false);
  const urlInvalid = useRef(false);
  const [logo, setLogo] = useState<Image | undefined>(sponsor?.logo);
  const [editing, setEditing] = useState(!sponsor);
  const [, forceValidationRender] = useState(0);

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const onSetImage = useCallback((img: Image) => {
    if (!editing) {
      return;
    }
    setLogo(img);
    toggleImagePicker();
  }, [editing, toggleImagePicker]);

  const isValidUrl = useCallback((urlString: string): boolean => {
    try {
      const parsedUrl = new URL(urlString);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    } catch {
      return false;
    }
  }, []);

  const errorStateActive = useRef(false);
  const saveDisabled = useMemo(() => {
    const nameInvalidCheck = name.length < 2;
    const urlInvalidCheck = url.length > 0 && !isValidUrl(url);
    nameInvalid.current = nameInvalidCheck;
    urlInvalid.current = urlInvalidCheck;
    
    if (sponsor) {

      return nameInvalidCheck ||
        (name === sponsor.name && url === sponsor.url && logo?.id === sponsor.logo?.id);
    }
    return nameInvalidCheck || !logo;
  }, [name, url, logo, sponsor, isValidUrl]);

  const onSave = useCallback(() => {
    errorStateActive.current = true;
    if (url.length > 0 && !isValidUrl(url)) {
      urlInvalid.current = true;
      forceValidationRender(prev => prev + 1);
      return;
    }
    if (loading || saveDisabled || !editing) {
      return;
    }

    if (sponsor) {
      setLoading(true);
      trackAmplitude("tm_patch_sponsor_finish", { teamId, sponsorId: sponsor.id });
      callClientFunc(
        SponsorService.patchSponsorSponsorSponsorIdPut,
        false,
        sponsor.id,
        {
          name: name !== sponsor.name ? name : undefined,
          url: url !== sponsor.url ? url : undefined,
          logoId: logo?.id !== sponsor.logo?.id ? logo?.id : undefined,
        }
      ).then((res) => {
        close(res);
      }, errorLogging).finally(() => {
        setLoading(false);
      });
    } else {
      if (!logo) {
        return;
      }
      setLoading(true);
      trackAmplitude("tm_create_sponsor_finish", { teamId });
      callClientFunc(
        SponsorService.createSponsorSponsorTeamTeamIdPost,
        false,
        teamId,
        {
          name,
          url,
          logoId: logo.id,
        }
      ).then((res) => {
        close(res);
      }, errorLogging).finally(() => {
        setLoading(false);
      });
    }
  }, [loading, saveDisabled, sponsor, name, url, logo, close, teamId, editing, isValidUrl, forceValidationRender]);

  const onDelete = useCallback(() => {
    if (sponsor) {
      setDeleting(true);
      trackAmplitude("tm_delete_sponsor", { sponsor: sponsor.id, team: teamId });
      callClientFunc(
        SponsorService.deleteSponsorSponsorSponsorIdDelete,
        false,
        sponsor.id
      ).then(() => {
        close(sponsor.id);
      }, errorLogging).finally(() => {
        setDeleting(false);
      });
    }
  }, [close, sponsor, teamId]);

  return (
    <AsideModal
      close={onClose}
      title={sponsor ? translate("modifySponsor", l) : translate("addSponsor", l)}
      noHeaderBorder
    >
      <div className="relative w-full">
        {!!sponsor && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="absolute top-0 right-0 text-primary hover:text-btn-active transition-colors text-sm font-medium"
          >
            {translate("edit", l)}
          </button>
        )}
      </div>
      <div className="mt-2 mb-6 flex flex-col items-center justify-center w-full">
        <button
          onClick={toggleImagePicker}
          type="button"
          disabled={!!sponsor && !editing}
          className="w-full aspect-video rounded-lg overflow-hidden bg-light-gray dark:bg-light-gray-dark border-2 border-dashed border-light-gray dark:border-light-gray-dark hover:border-primary dark:hover:border-primary flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {logo ? (
            <ClavaImage
              image={logo}
              width="100%"
              format="full"
              className="rounded-lg"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <FontAwesomeIcon
                icon={faImage}
                className="w-16 h-16 text-font-600 dark:text-font-600-dark mb-2"
              />
              <span className="text-sm text-font-600 dark:text-font-600-dark">
                {sponsor ? translate("edit", l) : translate("addSponsor", l)}
              </span>
            </div>
          )}
        </button>
        {logo && (
          <button
            onClick={toggleImagePicker}
            type="button"
            disabled={!!sponsor && !editing}
            className="mt-2 text-font dark:text-font-dark py-0.5 px-3 rounded-full border border-light-gray dark:border-light-gray-dark hover:bg-primary-10 flex flex-row items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {translate("edit", l)}
            <FontAwesomeIcon icon={faPencil} className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>
      <ClavaTextInput
        className="mt-4"
        onChange={setName}
        error={nameInvalid.current && errorStateActive.current}
        value={name}
        disabled={!!sponsor && !editing}
        label={`${translate("sponsorName", l)} *`}
        placeholder={translate("sponsorName", l)}
      />
      <ClavaTextInput
        className="mt-4"
        onChange={setUrl}
        error={urlInvalid.current && errorStateActive.current}
        value={url}
        disabled={!!sponsor && !editing}
        label={translate("sponsorLink", l)}
        placeholder="https://example.com"
      />
      {urlInvalid.current && errorStateActive.current && (
        <span className="mt-1 text-xs text-loss">
          {translate("sponsorInvalidLink", l)}
        </span>
      )}
      <div className="flex-1" />
      <div className="w-full mt-4 mb-2 flex flex-col gap-2 px-4">
        {sponsor ? (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-red active:bg-red hover:bg-red text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? (
              <img src={loadingGif} alt="Loading" className="w-4 h-4" />
            ) : (
              <span className="text-sm font-semibold whitespace-nowrap">
                {translate("deleteSponsor", l)}
              </span>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-red active:bg-red hover:bg-red text-white"
          >
            <span className="text-sm font-semibold whitespace-nowrap">
              {translate("cancel", l)}
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled || loading}
          className="w-full rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-primary active:bg-btn-active hover:bg-btn-active text-font-dark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <img src={loadingGif} alt="Loading" className="w-4 h-4" />
          ) : (
            <span className="text-sm font-semibold whitespace-nowrap">
              {translate("save", l)}
            </span>
          )}
        </button>
      </div>
      {imagePicker && (
        <FileUploader
          as={16 / 9}
          type="image"
          callback={onSetImage}
          close={toggleImagePicker}
          hideBorders
          hideUploadButton
        />
      )}
    </AsideModal>
  );
};

export default CreateEditSponsor;

